<script lang="ts">
	import type { FontProps } from '../types/fontProps'
	import { DefaultInputFieldProps, getFontPropsVarString } from '../types/fontProps'
	import Flatpickr from '$lib/atoms/datepicker/Flatpickr.svelte'
	import { openCloseVariants } from '../utils/svelteMotionUtils'
	import { AnimatePresence, AnimateSharedLayout, Motion } from 'svelte-motion'
	import type { Options } from 'flatpickr/dist/types/options'

	interface MaterialDatePickerProps {
		id?: string
		value?: string | string[]
		label: string
		placeholder?: string
		width?: string
		disabled?: boolean
		fontProps?: FontProps
		flatpickrOptions?: Options
		error?: string
		containerWidth?: string
		onchange?: (selectedDates: Date[], dateStr: string, instance: unknown) => void
		onopen?: (selectedDates: Date[], dateStr: string, instance: unknown) => void
		onclose?: (selectedDates: Date[], dateStr: string, instance: unknown) => void
	}

	let {
		id = '',
		value = $bindable(),
		label,
		placeholder = '',
		width = '100%',
		disabled = false,
		fontProps = DefaultInputFieldProps(),
		flatpickrOptions = {} as Options,
		error = '',
		containerWidth = '100%',
		onchange,
		onopen,
		onclose
	}: MaterialDatePickerProps = $props()

	let dateId: string = 'date-id' + Math.random().toString(16).slice(2)
	let open = $state(false) // toggles the border when the calendar is opened

	function handleOpen(selectedDates: Date[], dateStr: string, instance: unknown) {
		open = true
		onopen?.(selectedDates, dateStr, instance)
	}

	function handleClose(selectedDates: Date[], dateStr: string, instance: unknown) {
		open = false
		onclose?.(selectedDates, dateStr, instance)
	}

	const fontPropsStyleString = $derived(getFontPropsVarString(fontProps))
	const displayLabel = $derived(label || placeholder)
	const displayPlaceholder = $derived(label ? placeholder : '')
	// if value is not falsy value, notEmpty is true or if we have label AND placeholder
	const notEmpty = $derived(!!value || (placeholder && label))
</script>

<AnimateSharedLayout>
	<div class="input-field-error-message-container" style="--container-width: {containerWidth}">
		<div class="material-design" style="{fontPropsStyleString}; --input-width: {width}">
			<div
				class="input-field-focus date-time-field input"
				style="width: {width}; max-width: {width};{getFontPropsVarString(fontProps)}"
				class:open
				class:notEmpty
				class:material-disabled={disabled}
			>
				<Flatpickr
					{id}
					bind:value
					options={flatpickrOptions}
					placeholder={displayPlaceholder}
					{onchange}
					onopen={handleOpen}
					onclose={handleClose}
				/>
			</div>
			<label
				class="material-label"
				for={dateId}
				id="label-{dateId}"
				class:material-label-disabled={disabled}
			>
				<div class="material-text">{displayLabel}</div>
			</label>
		</div>
		<AnimatePresence list={error && error !== '' ? [{ key: 1 }] : []}>
			<Motion
				let:motion={collapse}
				initial="collapsed"
				animate="open"
				exit="collapsed"
				variants={openCloseVariants}
			>
				<span class="form-field-error" use:collapse>{@html error}</span>
			</Motion>
		</AnimatePresence>
	</div>
</AnimateSharedLayout>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/inputFields';

	.material-design > .material-label-disabled > .material-text {
		background-color: white !important;
	}

	.date-time-field {
		height: 2rem !important;
	}

	.date-time-field .input {
		height: 2rem !important;
		max-height: 2rem !important;
		background-color: white !important;

		& > .material-label-disabled,
		& > .material-disabled > input {
			background-color: var(--color-gray-50);
			cursor: not-allowed;
		}

		& > .material-disabled > input,
		& > .material-disabled > :global(input.flatpickr-input) {
			color: $grey-800;
			background-color: var(--color-gray-50);
			cursor: not-allowed;
		}
	}

	.open {
		border: 1px solid $primaryColor;
		outline: none !important;
	}

	div :global(.flatpickr-input) {
		border: none;
		box-shadow: none;
		height: 20px;
		padding: 0;
		width: var(--input-width);

		&:disabled,
		&:read-only,
		&[readonly],
		&[disabled] {
			background-color: white;
		}

		&::placeholder {
			color: var(--placeholder-color);
		}
	}

	div.open + .material-label .material-text {
		font-size: 0.75rem;
		font-weight: 400;
		transform: translate(0, -140%);
		background-color: white;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
		color: black;
	}

	:global(.flatpickr-day.selected) {
		background: $primaryColor !important;
		border-color: $primaryColor !important;
	}
</style>
